import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { fmt } from '../../lib/formatters';
import { toast } from '../../lib/toast';

// ─────────────────────────────────────────────────────────────────────
// «Crear nueva cotización» desde el listado de productos: una línea por
// producto seleccionado con el coste USD anterior (último pedido > última
// cotización > catálogo del proveedor) y el coste nuevo editable. Al crear,
// el servidor recalcula con cambio, transporte y condiciones vigentes y deja
// una cotización nueva por producto en Pdte. de enviar, con fecha de hoy.
// ─────────────────────────────────────────────────────────────────────
type CosteFuente = 'pedido' | 'cotizacion' | 'proveedor' | 'ninguno';
type FilaPreview = {
  producto_id: string; sku: string; nombre: string;
  base_cotizacion_id: string | null;
  coste_usd: number; coste_usd_anterior: number | null; coste_fuente: CosteFuente; coste_fecha: string | null;
  cambio_usd_eur: number; coste_transporte_unidad_eur: number;
  cdpe_eur: number; coste_game_final_eur: number; margen_approx_real_pct: number | null;
};
type Contexto = { cambio_usd_eur: number; precio_contenedor_eur: number };

function fuenteLabel(f: CosteFuente, fecha: string | null): string {
  const d = fecha ? ` del ${fmt.date(fecha)}` : '';
  switch (f) {
    case 'pedido': return `último pedido${d}`;
    case 'cotizacion': return 'última cotización';
    case 'proveedor': return `catálogo del proveedor${d}`;
    default: return 'sin coste previo';
  }
}

function parseNum(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export function CotizacionDesdeProductosModal({ productoIds, onClose, onCreated }: {
  productoIds: string[] | null;
  onClose: () => void;
  onCreated: (ids: string[]) => void;
}) {
  const [filas, setFilas] = useState<FilaPreview[] | null>(null);
  const [ctx, setCtx] = useState<Contexto | null>(null);
  const [costes, setCostes] = useState<Record<string, string>>({});

  const preview = useMutation({
    mutationFn: (body: { producto_ids: string[]; overrides?: Record<string, { coste_usd: number }> }) =>
      api.post<{ data: FilaPreview[]; contexto: Contexto }>('/precio/cotizaciones/desde-productos/preview', body),
    onSuccess: (r, vars) => {
      setFilas(r.data);
      setCtx(r.contexto);
      // Solo se rellenan los costes en la primera carga: luego manda lo tecleado.
      if (!vars.overrides) {
        setCostes(Object.fromEntries(r.data.map((f) => [f.producto_id, f.coste_usd_anterior != null ? String(f.coste_usd_anterior) : ''])));
      }
    },
    onError: (err) => toast.error('No se pudo calcular la previsualización', (err as Error).message),
  });

  const crear = useMutation({
    mutationFn: (items: Array<{ producto_id: string; coste_usd: number }>) =>
      api.post<{ count: number; ids: string[] }>('/precio/cotizaciones/desde-productos', { items }),
    onSuccess: (r) => {
      toast.success(
        r.count === 1 ? 'Cotización nueva creada' : `${r.count} cotizaciones nuevas creadas`,
        'Con fecha de hoy, en estado Pdte. de enviar.',
      );
      onCreated(r.ids);
    },
    onError: (err) => toast.error('No se pudieron crear las cotizaciones', (err as Error).message),
  });

  useEffect(() => {
    if (!productoIds || productoIds.length === 0) return;
    setFilas(null); setCtx(null); setCostes({});
    preview.mutate({ producto_ids: productoIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productoIds]);

  const overrides = (): Record<string, { coste_usd: number }> => {
    const o: Record<string, { coste_usd: number }> = {};
    for (const [pid, s] of Object.entries(costes)) {
      const n = parseNum(s);
      if (n != null && n > 0) o[pid] = { coste_usd: n };
    }
    return o;
  };
  const recalcular = () => {
    if (!productoIds) return;
    preview.mutate({ producto_ids: productoIds, overrides: overrides() });
  };

  const faltan = (filas ?? []).filter((f) => !((parseNum(costes[f.producto_id] ?? '') ?? 0) > 0));
  const onCrear = () => {
    if (!filas || faltan.length > 0) return;
    crear.mutate(filas.map((f) => ({ producto_id: f.producto_id, coste_usd: parseNum(costes[f.producto_id])! })));
  };

  const n = productoIds?.length ?? 0;
  return (
    <Modal
      open={!!productoIds && n > 0}
      onClose={onClose}
      title={n === 1 ? 'Crear nueva cotización' : `Crear ${n} cotizaciones nuevas`}
      size="xl"
      footer={
        <>
          <Button variant="danger" onClick={onClose} disabled={crear.isPending}>Cancelar</Button>
          <Button variant="secondary" onClick={recalcular} disabled={preview.isPending || !filas}>
            {preview.isPending ? '↻ Calculando…' : '↻ Recalcular'}
          </Button>
          <Button variant="create" onClick={onCrear} disabled={crear.isPending || !filas || faltan.length > 0}>
            {crear.isPending ? 'Creando…' : n === 1 ? 'OK, crear cotización' : 'OK, crear cotizaciones'}
          </Button>
        </>
      }
    >
      {!filas ? (
        <p className="py-6 text-center text-sm text-slate-400">Buscando el último coste de cada producto…</p>
      ) : (
        <div className="space-y-3 text-sm">
          <p className="text-xs text-slate-500">
            Escribe el <strong>precio de coste nuevo</strong> (USD de fábrica). Se recalcula con las condiciones
            vigentes{ctx ? <>: cambio <span className="font-mono">{ctx.cambio_usd_eur.toFixed(4)}</span>, contenedor <span className="font-mono">{fmt.eur(ctx.precio_contenedor_eur)}</span></> : ''}, transporte y condiciones comerciales del cliente.
          </p>
          {faltan.length > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
              Falta el coste nuevo en {faltan.length} producto{faltan.length === 1 ? '' : 's'}.
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <th className="py-1.5 pr-3">Producto</th>
                  <th className="py-1.5 pr-3 text-right">Coste anterior</th>
                  <th className="py-1.5 pr-3 text-right">Coste nuevo USD</th>
                  <th className="py-1.5 pr-3 text-right">Transporte ud</th>
                  <th className="py-1.5 pr-3 text-right">CDPe</th>
                  <th className="py-1.5 pr-3 text-right">Coste final</th>
                  <th className="py-1.5 text-right">Margen</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f) => (
                  <tr key={f.producto_id} className="border-b border-slate-100 align-top">
                    <td className="py-1.5 pr-3">
                      <div className="font-mono text-xs text-slate-500">{f.sku}</div>
                      <div className="max-w-xs truncate text-slate-900" title={f.nombre}>{f.nombre}</div>
                      {!f.base_cotizacion_id && <div className="text-[10px] text-slate-400">Primera cotización del producto</div>}
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <div className="font-mono">{f.coste_usd_anterior != null ? fmt.usd(f.coste_usd_anterior) : '—'}</div>
                      <div className="text-[10px] text-slate-400">{fuenteLabel(f.coste_fuente, f.coste_fecha)}</div>
                    </td>
                    <td className="py-1.5 pr-3 text-right">
                      <Input
                        type="number"
                        step="any"
                        min="0"
                        value={costes[f.producto_id] ?? ''}
                        onChange={(e) => setCostes((s) => ({ ...s, [f.producto_id]: e.target.value }))}
                        onBlur={recalcular}
                        onKeyDown={(e) => { if (e.key === 'Enter') recalcular(); }}
                        className="w-28 text-right font-mono"
                        aria-label={`Coste nuevo USD de ${f.sku}`}
                      />
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono">{fmt.eur(f.coste_transporte_unidad_eur)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono">{fmt.eur(f.cdpe_eur)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono font-semibold">{fmt.eur(f.coste_game_final_eur)}</td>
                    <td className="py-1.5 text-right font-mono">{f.margen_approx_real_pct != null ? `${f.margen_approx_real_pct.toFixed(1)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
